/* eslint-disable react/no-unescaped-entities */

import Head from "next/head";

import Footer from "@/components/marketing/pieces/Footer";
import NavbarLegacy from "@/components/marketing/pieces/NavbarLegacy";

export default function TermsConditions() {
  return (
    <>
      <Head>
        <title>Terms of Service | Rebolt</title>
        <meta
          name="description"
          content="Review Rebolt's terms of service for using our marketing platform for home service businesses. Understand your rights and responsibilities as a user."
        />
        <meta
          property="og:image"
          content="https://api.screenshotone.com/take?access_key=cQWOH7FyQTatDw&url=https://rebolthq.com/terms&full_page=false&viewport_width=1200&viewport_height=850&device_scale_factor=1&format=jpg&image_quality=80&cache=true&cache_ttl=304800"
        />
      </Head>
      <NavbarLegacy />
      <main className="container prose pt-16">
        <h1 className="font-bold mt-14">Terms of Service</h1>

        <p>
          <strong>Company Name:</strong> RedNine, Inc. d/b/a Rebolt
        </p>
        <p>
          <strong>Contact:</strong> support@rebolthq.com
        </p>

        <hr />

        <h2>1. Acceptance of Terms</h2>
        <p>
          By accessing or using Rebolt's services, software, websites, tools, or platforms, you agree to be bound by
          these Terms of Service. These Terms constitute a binding agreement between you ("Client") and RedNine, Inc., a
          Delaware corporation ("Rebolt").
        </p>

        <hr />

        <h2>2. Customer Responsibilities</h2>
        <p>You agree that:</p>
        <ul>
          <li>
            You are responsible for providing accurate and complete business information, assets, and access required
            for service delivery (including domains, hosting access, Google Business Profile, social accounts, images,
            logos, and content).
          </li>
          <li>Delays caused by missing access or delayed responses are not grounds for refunds or disputes.</li>
          <li>All payments are non-refundable once services have commenced.</li>
          <li>Marketing performance, rankings, traffic, and lead volume are not guaranteed and vary by market.</li>
          <li>You assume all risk related to your business operations and online presence.</li>
        </ul>

        <hr />

        <h2>3. Accessibility & ADA Compliance</h2>
        <p>
          Rebolt does <strong>not</strong> represent or warrant that any website, content, design, template, or service
          provided is compliant with the Americans with Disabilities Act (ADA), WCAG guidelines, or any other
          accessibility laws, regulations, or standards.
        </p>
        <p>You acknowledge and agree that:</p>
        <ul>
          <li>
            <strong>You are solely responsible</strong> for ensuring your website and business comply with all
            applicable accessibility, disability access, and consumer protection laws.
          </li>
          <li>
            Accessibility requirements vary by jurisdiction and business type, and Rebolt does not provide legal advice
            or compliance certification.
          </li>
          <li>
            Any accessibility tools, plugins, recommendations, or design choices offered by Rebolt are provided "as-is"
            and do not constitute a guarantee of compliance.
          </li>
        </ul>
        <h3>Indemnification for Accessibility Claims</h3>
        <p>
          You agree to <strong>indemnify, defend, and hold harmless</strong> Rebolt, its officers, employees,
          contractors, and affiliates from any claims, demands, damages, losses, costs, or legal fees arising out of or
          related to:
        </p>
        <ul>
          <li>Alleged ADA or accessibility violations</li>
          <li>WCAG compliance claims</li>
          <li>Demand letters, lawsuits, or regulatory actions related to website accessibility</li>
          <li>Any claim asserting that your website or online presence is non-compliant with accessibility laws</li>
        </ul>

        <hr />

        <h2>4. Intellectual Property & License</h2>
        <p>
          You retain ownership of all business information, images, logos, and content you provide. You also own the
          final written content created specifically for your business.
        </p>
        <p>However:</p>
        <ul>
          <li>
            Rebolt retains ownership of all underlying website structure, templates, layouts, code, systems, automation,
            and proprietary technology.
          </li>
          <li>
            You are granted a limited, non-transferable license to use your website only while your subscription is
            active.
          </li>
          <li>You may not copy, replicate, resell, or reverse engineer Rebolt's systems or designs.</li>
        </ul>

        <hr />

        <h2>5. Website Buyout Option</h2>
        <p>
          Clients may elect to purchase a one-time <strong>website buyout</strong> for a total cost of{" "}
          <strong>$5,000 USD</strong>.
        </p>
        <p>If purchased, the buyout includes:</p>
        <ul>
          <li>A one-time export of the website files as-is at the time of purchase</li>
          <li>Delivery of static files suitable for third-party hosting</li>
          <li>Transfer of responsibility for hosting, maintenance, updates, security, and compliance</li>
        </ul>
        <p>The buyout does not include:</p>
        <ul>
          <li>Ongoing support or maintenance</li>
          <li>Rebolt's proprietary backend systems, automation, dashboards, or tools</li>
          <li>Continued access to Rebolt software or services</li>
          <li>Future updates or optimizations</li>
        </ul>
        <p>
          Upon completion of the buyout and delivery of files, Rebolt has no further obligations related to the website.
        </p>

        <hr />

        <h2>6. Use Restrictions</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Copy or mirror Rebolt-built websites on other platforms without a buyout</li>
          <li>Reverse engineer or access proprietary systems</li>
          <li>Grant unauthorized third-party access</li>
          <li>Use Rebolt services for illegal, deceptive, or unethical purposes</li>
        </ul>
        <p>Rebolt may suspend or terminate services for violations.</p>

        <hr />

        <h2>7. Subscription, Billing & Cancellation</h2>
        <ul>
          <li>All fees are billed in advance and are non-refundable.</li>
          <li>Annual subscription charges are non-refundable.</li>
          <li>
            To cancel, you must email support@rebolthq.com at least <strong>7 days prior</strong> to your next billing
            date.
          </li>
          <li>Failed payments may result in immediate suspension of services, including hosting.</li>
          <li>Upon cancellation or non-payment, your website may be taken offline.</li>
        </ul>

        <hr />

        <h2>8. Disputes & Chargebacks</h2>
        <p>
          You agree to contact Rebolt before initiating any chargeback or payment dispute. You may not dispute charges
          based on dissatisfaction with marketing results or business performance.
        </p>

        <hr />

        <h2>9. Content Liability</h2>
        <p>
          You are solely responsible for all content published on your website, including compliance with intellectual
          property, advertising, consumer protection, accessibility, and industry-specific regulations.
        </p>
        <p>
          You agree to indemnify and hold Rebolt harmless from any claims arising from your content or business
          operations.
        </p>

        <hr />

        <h2>10. Disclaimers & Limitation of Liability</h2>
        <p>
          Services are provided "as-is" without warranties of any kind. Rebolt does not guarantee uninterrupted service,
          performance outcomes, or regulatory compliance.
        </p>
        <p>
          To the fullest extent permitted by law, Rebolt's total liability shall not exceed the amounts paid by you in
          the prior 60 days.
        </p>

        <hr />

        <h2>11. Termination</h2>
        <p>
          Rebolt may terminate or suspend access for violations, abuse, fraud, or non-payment. Termination does not
          release you from prior obligations.
        </p>

        <hr />

        <h2>12. Modifications</h2>
        <p>
          Rebolt may update these Terms at any time. Continued use of services constitutes acceptance of revised terms.
        </p>

        <hr />

        <h2>13. Governing Law</h2>
        <p>
          These Terms are governed by the laws of the State of <strong>Delaware</strong>, with venue in{" "}
          <strong>Wilmington, Delaware</strong>.
        </p>

        <hr />

        <h2>14. Contact</h2>
        <p>
          <strong>RedNine, Inc. d/b/a Rebolt</strong>
        </p>
        <p>📧 support@rebolthq.com</p>
      </main>
      <Footer />
    </>
  );
}
